import { useLingui } from '@lingui/react/macro';

import { legacyDate, legacyDocument, legacyMonthYear, legacyReais } from '../credit-note.legacy-format';
import { dataprevKey, zetraKey, type DataprevEmployee, type DataprevMargin, type ZetraMargin } from '../credit-note.margin';
import type { MarginCardOption } from './margin-panel';

// Os cartões das margens do Crédito do Trabalhador (Dataprev) e do Zetra (Summary/Zetra.tsx e TooltipZetra.tsx), os mesmos
// na Operação (9A) e na Simulação (R4): uma formatação só, pela porta (R5).

type ZetraServer = NonNullable<NonNullable<ZetraMargin['result']>['servidores']>[number];

/** "Competência atual" do tooltip (TooltipZetra.tsx:137-142): `202609` → `09/2026`. */
function competence(item: DataprevEmployee): string {
  const value = item.CompetenciaAtual ?? item.competenciaAtual;
  return legacyMonthYear(value === null || value === undefined ? undefined : String(value)) ?? 'N/D';
}

const orNdText = (value: string | null | undefined) => (value === null || value === undefined || value.trim() === '' ? 'N/D' : value);

export function useDataprevMarginOptions(margin: DataprevMargin | undefined): MarginCardOption[] {
  const { i18n, t } = useLingui();
  const date = legacyDate(margin?.createdAt, i18n.locale) ?? '';
  return (margin?.result ?? []).map((item) => {
    const cpf = legacyDocument(item.cpf === null || item.cpf === undefined ? undefined : String(item.cpf)) ?? '';
    const value = legacyReais(item.valorMargemDisponivel, i18n.locale);
    const matricula = item.matricula ?? '';
    return {
      key: dataprevKey(item),
      // Summary/Zetra.tsx:22-26.
      title: `${item.nome ?? ''} - ${cpf}`,
      description: t`Matrícula: ${matricula} | Data da consulta: ${date} | Valor da margem: ${value}`,
      // TooltipDataprevEmployee (TooltipZetra.tsx:131-180).
      details: [
        [t`Nome`, orNdText(item.nome)],
        [t`CPF`, orNdText(cpf)],
        [t`CNPJ do empregador`, orNdText(legacyDocument(item.numeroInscricaoEmpregador === null || item.numeroInscricaoEmpregador === undefined ? undefined : String(item.numeroInscricaoEmpregador)))],
        [t`Sexo`, orNdText(item.sexo?.descricao)],
        [t`Competência atual`, competence(item)],
        [t`Matricula`, orNdText(item.matricula)],
      ],
    };
  });
}

const orNd = (value: unknown) => (typeof value === 'string' && value.trim() !== '' ? value : 'N/D');

export function useZetraMarginOptions(margin: ZetraMargin | undefined): MarginCardOption[] {
  const { i18n, t } = useLingui();
  const date = legacyDate(margin?.createdAt, i18n.locale) ?? '';
  return (margin?.result?.servidores ?? []).flatMap((server: ZetraServer) => {
    const margins = server.margens ?? [];
    // TooltipZetra (TooltipZetra.tsx:40-120): o vínculo e o valor de cada margem.
    const details: (readonly [string, string])[] = [
      [t`Nome`, orNd(server.orgao)],
      [t`Código do órgão`, orNd(server.orgaoCodigo)],
      [t`CPF`, orNd(legacyDocument(server['cpf']))],
      [t`Estabelecimento`, orNd(server['estabelecimento'])],
      [t`Categoria`, orNd(server['categoria'])],
      [t`Matricula`, orNd(server.matricula)],
      ...margins.map((item, index) => [margins.length > 1 ? t`Valor da margem (${String(index + 1)})` : t`Valor da margem`, legacyReais(item.valorDisponivel, i18n.locale)] as const),
    ];
    return margins.map((item) => {
      const matricula = server.matricula ?? '';
      const value = legacyReais(item.valorDisponivel, i18n.locale);
      return {
        key: zetraKey(server, item),
        // Summary/Zetra.tsx:80-85.
        title: `${server.orgao ?? ''} - ${item.descricao ?? ''}`,
        description: t`Matrícula: ${matricula} | Data da consulta: ${date} | Valor da margem: ${value}`,
        details,
      };
    });
  });
}
